import Vue from 'vue';
import { createHobbitApp } from 'hobbit-base';
import {
  setLS, getMainDomain, sampling, loadJS, setCookie,
} from 'hobbit-base/modules/utils';
// 常用全局通用组件
import AlloyFinger from 'alloyfinger';
import AlloyFingerPlugin from 'alloyfinger/vue/alloy_finger_vue';
import appView from './app/app.vue'; // App.vue 页面母版
import User from './modules/user'; // 鉴权和用户基本属性相关模块

// eslint-disable-next-line import/no-dynamic-require
const config = require(`../config/${process.env.VUE_APP_RUNTIME}`);

createHobbitApp(
  appView,
  config,
  ({ $hobbit, router }) => {
    const $config = $hobbit.$config;
    const $ctx = $hobbit.$ctx;
    const $log = $hobbit.$log;
    const $resources = $hobbit.$resources;
    const $abtest = $hobbit.$abtest;
    const $fetch = $hobbit.$fetch;
    // #region =#=#=#=#=#=#=#=#=#= 扩展项目级别基础模块 =#=#=#=#=#=#=#=#=#=
    const $user = new User($config, $fetch, $ctx);
    Vue.prototype.$user = $user;
    Vue.use(AlloyFingerPlugin, { AlloyFinger });
    // #endregion

    // #region =#=#=#=#=#=#=#=#=#= 初始化运行环境上下文 =#=#=#=#=#=#=#=#=#=
    if ($ctx.isProd()) {
      if (/^pre-/i.test(window.location.host)) {
        $ctx.isProd = () => false;
        $ctx.isPre = () => true;
      }
    }
    // #endregion

    // #region =#=#=#=#=#=#=#=#=#= 初始化国家和语言、时区 =#=#=#=#=#=#=#=#=#=
    // 计算实时时差
    const tzOffset = new Date().getTimezoneOffset();
    const tzHH = `0${parseInt(Math.abs(tzOffset) / 60, 10)}`.slice(-2);
    const tzMM = `0${Math.abs(tzOffset) % 60}`.slice(-2);
    $ctx.vkTimezone = `GMT${tzOffset > 0 ? '-' : '+'}${tzHH}:${tzMM}`;

    $ctx.setBiz = biz => {
      if ($config.site.supportBizs.some(s => s.biz === biz)) {
        $ctx.biz = biz;
        setLS($config.site.bizKey, biz);
      }
    };
    $ctx.setBiz($config.site.supportBizs[0].biz);

    // 语言初始化逻辑 Path -> Cookie -> Default
    (function initLang() {
      $ctx.LANG_PATH_REGEXP = /^\/([a-z]{2}-[a-z]{2})\//i;
      $ctx.getLangByPath = (path = window.location.pathname) => {
        const pathLang = $ctx.LANG_PATH_REGEXP.exec(path);
        if (pathLang && pathLang[1]) return pathLang[1].toLowerCase();
        return null;
      };

      $ctx.setLang = (lang, updateURL = 1, updateApp = 1) => new Promise((resolve, reject) => {
        $ctx.lang = lang;

        // 持久化到Cookie
        setCookie($config.site.langKey, lang, new Date().dateAdd(3650), '/');

        // 更新URL
        if (updateURL && window.history.pushState) {
          const oldLang = $ctx.getLangByPath();
          if (!oldLang || oldLang !== lang) {
            const replacePath = `${window.location.pathname.replace($ctx.LANG_PATH_REGEXP, `/${lang}/`)}${window.location.search}${window.location.hash}`;
            window.history.replaceState(null, null, replacePath);
          }
        }

        // 同步到PC-App
        if (updateApp && $ctx.client === $ctx.CLIENT.VKPC) {
          if (window.Chinook && window.Chinook.getLanguageInfo) {
            const newPcAppLangCode = $config.site.supportLangs.find(
              o => o.lang === lang,
            ).pcAppLangCode;
            const pcAppLangCode = window.Chinook.getLanguageInfo();
            if (pcAppLangCode !== newPcAppLangCode) {
              window.Chinook.saveLanguageInfo(newPcAppLangCode);
            }
          } else {
            console.warn('no Chinook.getLanguageInfo');
          }
        }

        // 加载新语言资源
        $resources.$installer.install(lang).then(resolve).catch(reject);
      });

      $ctx.lang = $ctx.getLangByPath() ?? $config.site.supportLangs[0].lang;
    }());
    // #endregion

    // #region =#=#=#=#=#=#=#=#=#= LOG模块初始化 =#=#=#=#=#=#=#=#=#=
    (function initLog() {
      // 设置神策用户ID
      const userId = $user.vkUserId();
      if (userId) {
        $log.saReady.then(sensors => {
          if (sensors) {
            sensors.login(userId);
          }
        });
      }
      // 设置项目通用埋点固定数据
      $log.setCommonParams({
        product: $config.log.product,
        business: $config.log.appName,
        $url: window.location.href,
        vksessionid: $ctx.sessionID ?? '',
        vkdeviceid: $ctx.deviceID ?? '',
        vksys: $ctx.system,
        vkclient: $ctx.client,
        vkvt: $ctx.vt,
        $app_version: $ctx.vkVersion,
        runtime: $ctx.runtime,
      });
      // 设置通用埋点动态数据
      $log.beforeSendStat = (eventType, params) => {
        const nowTime = new Date().getTime();
        params.timestamp = nowTime;
        params.pageIndex = $ctx.isNewDevice ? -1 : $ctx.pageIndex;
        if ($ctx.startPage) {
          params.startpage = $ctx.startPage.name;
          params.startspan = nowTime - $ctx.startPage.time; // 打开站点第一个页面到目前的总时长
        }
        if ($ctx.currPage) {
          params.currpage = $ctx.currPage.name;
          params.currspan = nowTime - $ctx.currPage.time; // 距离当前页面打开的时长
          if ($ctx.prevPage) {
            params.prevpage = $ctx.prevPage.name;
            params.prevspan = $ctx.currPage.time - $ctx.prevPage.time; // 前个页面停留时长
          }
        }
        params.vkuserid = $user.vkUserId() ?? '';
        params.student_id = $user.vkStudentId() ?? '';
        params.urlhash = window.location.hash;
        params.vklang = $ctx.lang;
        params.crcode = $ctx.biz;
        params.vktz = $ctx.vkTimezone;
        params.chfrom = $ctx.channel ?? '';
        params.refcode = $ctx.referralCode ?? '';
        // 渠道级别上报逻辑（支持.隔开多个级别）
        if ($ctx.channelLevel) {
          $ctx.channelLevel.split('.').forEach((level, index) => {
            params[`chlevel_${$log.LETTER_SEQUENCE[index]}`] = level;
          });
        } else {
          params.chlevel_a = 'S';
        }
        params.vkab = $log.bean2LogString($abtest.getABRecord());
        params.signin = $user.vkToken() ? 1 : 0; // 登录态（由于不能异步获取，因此不能使用$user.isLogin()来判断）
      };
      // 实现自动上报页面PV的方法
      $log.autoSendPV = (vue) => {
        const islanding = ($ctx.prevPage === null
          && getMainDomain(document.referrer).toLowerCase() !== getMainDomain().toLowerCase())
          ? 1 : 0;
        $log.view({
          event_id: vue.$route.meta.pv,
          $referrer: document.referrer,
          $title: document.title,
          islanding,
        });
      };
    }());

    // 初始化GA
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    if (window.top === window && $ctx.isProd()) {
      const gtmId = 'GTM-TCQNTDP';
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      window.gtag('js', new Date());
      $ctx.onload.then(() => {
        window.gtag('config', gtmId, { send_page_view: false });
        router.afterEach(to => { window.gtag('config', gtmId, { page_path: to.path }); });
        loadJS(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`, 1e4).catch(error => {
          console.warn('load GA failed', error);
        });
      });
    }
    // #endregion

    // #region =#=#=#=#=#=#=#=#=#= Fetch模块初始化 =#=#=#=#=#=#=#=#=#=
    (function initFetch() {
      // 接口请求AOP
      $fetch.hooks.beforeRequest = item => {
        if (!item.preventDefaultHeader) {
          // 追加通用header
          let vkDevice = $ctx.client === $ctx.VKPC ? 'PC_APP' : 'PC';
          if ($ctx.vt === $ctx.VIEW_TYPE.M) vkDevice = 'H5';
          item.headers = {
            'vk-device': vkDevice,
            'vk-session-id': $ctx.sessionID ?? '',
            'vk-cr-code': $ctx.biz,
            'vk-language': $ctx.lang,
            'vk-timezone': $ctx.vkTimezone,
            Authorization: $user.vkToken(),
            ...item.headers,
          };
        }
      };

      // 接口响应AOP
      $fetch.hooks.afterResponse = response => {
        // 调试输出
        if (response.source !== $fetch.SOURCE.AJAX && !$ctx.isProd()) {
          console.log(`%c$fetch:${response.source} ${response.options.url}`, 'color:#6f6', response);
        }

        // 接口监控统计
        const checkout = response.options.apiLogSampling ?? $config.log.defaultApiLogSampling;
        const hitSampling = sampling(checkout);
        if (response.errorCode || hitSampling) {
          let content = null;
          try {
            content = response.errorCode ? JSON.stringify(response.options.params) : undefined;
          } catch (ex) {
            content = String(ex);
          }
          $log.api({
            event_id: `${response.options.method}-${response.options.url}`,
            errorCode: response.errorCode,
            errorMessage: response.errorMessage,
            code: response.httpCode,
            message: response.httpMessage,
            duration: response.endTime - response.startTime,
            content,
            source: response.source,
            qos: response.retry ? 1 : 0,
            checkout: hitSampling ? checkout : undefined,
          });
        }

        // 未登录, 如果没指定忽略401，则自动引导登录
        if (response.errorCode === 401) {
          // 删除本地登录态信息
          $user.vkToken(null);
          if (!response.options.preventDefaultAuthCheck) {
            $user.gotoLogin();
          }
        }
      };
    }());
    // #endregion

    // #region =#=#=#=#=#=#=#=#=#= 其它杂项初始化 =#=#=#=#=#=#=#=#=#=
    // MOCK配置
    if ($config.fetch.mock) {
      import(/* webpackChunkName: "mocks" */'./mocks').then(mocks => {
        $fetch.mockConfig = mocks.default;
      });
    }

    // 加载调试控制台
    if ($config.site.vconsole && window.top === window) {
      loadJS('//s.vipkidstatic.com/fe-static/hobbit/asset/lib/vconsole3.9.min.js').then(() => {
        window.vConsole = new window.VConsole();
      });
    }
    // #endregion
  },
);
